/**
 * PickUpWhereYouLeftOff — Feb 28, 2026
 *
 * Surfaces the top 3 active personal sticky notes at the top of the provider
 * home screen, so when a nurse logs back in she sees her open work
 * immediately instead of having to open the floating sticky widget.
 *
 * Behavior:
 *   • Only renders for authed (non-guest) users.
 *   • Fetches /api/me/todos?show_done=false on mount.
 *   • Re-fetches whenever a `personal-todos-changed` custom event fires
 *     (dispatched by HistoryTodoList when items mutate elsewhere).
 *   • Hides if the user has zero active items.
 *   • Three per-item actions:
 *       ✓ Mark done           → PATCH /api/me/todos/{id} (done:true)
 *       ↪ Insert into notes    → appends "• {text}" to the notes textarea
 *       ⏰ Snooze until tomorrow → session-only hide (not server-side)
 *   • One panel-level action:  ✕ Dismiss panel for today (localStorage flag)
 *
 * Privacy: same PHI-handling assumptions as the rest of the sticky-note
 * feature — see PHI hint in HistoryTodoList.jsx.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Check, X, FileEdit, Clock, ListTodo } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../AuthProvider';

const API = window.__WR_API_BASE__;
const DISMISS_KEY = 'writeright_pickup_dismissed_until';
const SNOOZE_KEY = 'writeright_pickup_snoozed_until'; // map id -> timestamp

const _todayStartMs = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};
const _tomorrowStartMs = () => _todayStartMs() + 24 * 60 * 60 * 1000;

const _loadSnoozedMap = () => {
  try {
    const raw = localStorage.getItem(SNOOZE_KEY);
    if (!raw) return {};
    const obj = JSON.parse(raw);
    // Drop expired snoozes so the map doesn't grow unbounded.
    const now = Date.now();
    const fresh = Object.fromEntries(
      Object.entries(obj).filter(([, ts]) => ts > now)
    );
    if (Object.keys(fresh).length !== Object.keys(obj).length) {
      localStorage.setItem(SNOOZE_KEY, JSON.stringify(fresh));
    }
    return fresh;
  } catch { return {}; }
};

const _isDismissedToday = () => {
  try {
    const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    return until > Date.now();
  } catch { return false; }
};

export const PickUpWhereYouLeftOff = ({ notes, setNotes }) => {
  const { isAuthenticated, isGuest } = useAuth();
  const enabled = isAuthenticated && !isGuest;

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(() => _isDismissedToday());
  const [snoozedMap, setSnoozedMap] = useState(_loadSnoozedMap);

  // Fetch the user's top active sticky notes.
  const fetchActive = useCallback(async () => {
    if (!enabled) return;
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/api/me/todos?show_done=false`,
        { withCredentials: true }
      );
      const all = (res.data?.items || []).filter(i => !i.done);
      // Server already sorts by updated_at desc.
      setItems(all);
    } catch (err) {
      // Network down / 5xx — just hide silently.
      console.warn('PickUpWhereYouLeftOff: list fetch failed', err?.message);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled || dismissed) return;
    fetchActive();
    const handleChange = () => fetchActive();
    window.addEventListener('personal-todos-changed', handleChange);
    return () => window.removeEventListener('personal-todos-changed', handleChange);
  }, [enabled, dismissed, fetchActive]);

  const markDone = useCallback(async (item) => {
    setItems(prev => prev.filter(i => i.id !== item.id));
    try {
      await axios.patch(
        `${API}/api/me/todos/${item.id}`,
        { done: true },
        { withCredentials: true }
      );
      // Tell the floating widget so it refetches.
      window.dispatchEvent(new CustomEvent('personal-todos-changed'));
      toast.success('Marked done', { duration: 1500 });
    } catch (err) {
      toast.error('Could not mark done — your change is local.');
      // Rollback display so the user sees it still pending.
      fetchActive();
    }
  }, [fetchActive]);

  const snoozeUntilTomorrow = useCallback((item) => {
    const newMap = { ...snoozedMap, [item.id]: _tomorrowStartMs() };
    setSnoozedMap(newMap);
    try {
      localStorage.setItem(SNOOZE_KEY, JSON.stringify(newMap));
    } catch { /* quota — non-fatal */ }
    toast.info('Snoozed until tomorrow', { duration: 1500 });
  }, [snoozedMap]);

  const insertIntoNotes = useCallback((item) => {
    if (typeof setNotes !== 'function') return;
    const trimmed = (item.text || '').trim();
    if (!trimmed) return;
    const sep = (notes && notes.length > 0 && !notes.endsWith('\n')) ? '\n' : '';
    setNotes((notes || '') + `${sep}• ${trimmed}\n`);
    toast.success('Added to notes', { duration: 1500 });
  }, [notes, setNotes]);

  const dismissPanel = useCallback(() => {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, String(_tomorrowStartMs()));
    } catch { /* non-fatal */ }
  }, []);

  if (!enabled || dismissed) return null;
  // Filter out items the user snoozed; show at most 3.
  const visible = items.filter(i => !snoozedMap[i.id]).slice(0, 3);
  if (loading || visible.length === 0) return null;

  return (
    <div
      className="rounded-lg border-2 border-amber-400/40 bg-amber-500/8 px-3 py-2.5 shadow-sm"
      data-testid="pickup-where-left-off"
    >
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-1.5 text-amber-200">
          <ListTodo className="w-3.5 h-3.5" />
          <span className="text-xs font-semibold uppercase tracking-wider">
            Pick up where you left off
          </span>
          <span className="text-[10px] text-amber-200/60 normal-case">
            ({visible.length} open)
          </span>
        </div>
        <button
          type="button"
          onClick={dismissPanel}
          className="text-amber-200/60 hover:text-amber-200 text-xs"
          title="Hide for today"
          data-testid="pickup-dismiss-panel"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <ul className="flex flex-col gap-1.5">
        {visible.map((item) => (
          <li
            key={item.id}
            className="flex items-center gap-2 px-2 py-1.5 rounded bg-card/60 border border-amber-400/15"
            data-testid={`pickup-item-${item.id}`}
          >
            <button
              type="button"
              onClick={() => markDone(item)}
              className="shrink-0 w-5 h-5 rounded-full border border-emerald-400/40 hover:bg-emerald-500/20 flex items-center justify-center text-emerald-300"
              title="Mark done"
              data-testid={`pickup-done-${item.id}`}
            >
              <Check className="w-3 h-3" />
            </button>
            <span className="flex-1 text-sm text-foreground/90 truncate">
              {item.text}
            </span>
            <button
              type="button"
              onClick={() => insertIntoNotes(item)}
              className="shrink-0 text-[10px] px-1.5 py-1 rounded text-amber-200/80 hover:text-amber-100 hover:bg-amber-500/15 transition-colors flex items-center gap-1"
              title="Insert into notes"
              data-testid={`pickup-insert-${item.id}`}
            >
              <FileEdit className="w-3 h-3" />
              <span className="hidden sm:inline">Use</span>
            </button>
            <button
              type="button"
              onClick={() => snoozeUntilTomorrow(item)}
              className="shrink-0 text-[10px] px-1.5 py-1 rounded text-muted-foreground hover:text-foreground hover:bg-secondary/70 transition-colors flex items-center gap-1"
              title="Snooze until tomorrow"
              data-testid={`pickup-snooze-${item.id}`}
            >
              <Clock className="w-3 h-3" />
              <span className="hidden sm:inline">Later</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default PickUpWhereYouLeftOff;
